// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Save, RotateCcw, Clock } from "lucide-react";
import { formatCalendarDate } from "@/lib/dates";
import { formatTime } from "@/lib/dates";
import {
  resolveAvailability,
  computeCapacity,
  windowMinutes,
} from "@/lib/eventAvailability";

const hhmm = (t: string) => String(t ?? "").slice(0, 5);

const durationLabel = (mins: number) =>
  `${Math.floor(mins / 60)}h ${mins % 60}m`;

interface WindowRow {
  id?: string;
  session_date: string;
  start_time: string;
  end_time: string;
}

/** Editor for a set of windows (either the event defaults, or one court's). */
function WindowEditor({
  rows,
  onChange,
  defaultDate,
}: {
  rows: WindowRow[];
  onChange: (rows: WindowRow[]) => void;
  defaultDate?: string;
}) {
  const patch = (i: number, p: Partial<WindowRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));

  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            className="w-44"
            value={r.session_date ?? ""}
            onChange={(e) => patch(i, { session_date: e.target.value })}
          />
          <Input
            type="time"
            className="w-32"
            value={hhmm(r.start_time)}
            onChange={(e) => patch(i, { start_time: e.target.value })}
          />
          <span className="text-muted-foreground">to</span>
          <Input
            type="time"
            className="w-32"
            value={hhmm(r.end_time)}
            onChange={(e) => patch(i, { end_time: e.target.value })}
          />
          <span className="text-xs text-muted-foreground">
            {r.start_time && r.end_time
              ? durationLabel(windowMinutes(r.start_time, r.end_time))
              : ""}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          onChange([
            ...rows,
            {
              session_date:
                rows[rows.length - 1]?.session_date ||
                defaultDate ||
                new Date().toISOString().slice(0, 10),
              start_time: "08:00",
              end_time: "20:00",
            },
          ])
        }
      >
        <Plus className="mr-1 h-4 w-4" /> Add window
      </Button>
    </div>
  );
}

export default function AvailabilityTab({
  eventId,
  event,
  courts,
}: {
  eventId: string;
  event: any;
  courts: any[];
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  // Game timing lives on event_format_config — the same row the scheduler's
  // slot builder reads. Never on events.*.
  const formatQuery = useQuery({
    queryKey: ["avail-format", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_format_config")
        .select("event_id, game_duration_minutes, buffer_minutes_between_games, min_rest_minutes, max_games_per_day, back_to_back_minutes, max_gap_minutes")
        .eq("event_id", eventId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
  const formatCfg = formatQuery.data;

  const [gameLength, setGameLength] = useState<string>("");
  const [turnaround, setTurnaround] = useState<string>("");
  const [minRest, setMinRest] = useState<string>("0");
  const [maxPerDay, setMaxPerDay] = useState<string>("");
  const [backToBack, setBackToBack] = useState<string>("0");
  // Blank = no limit. A preference, never a hard rule.
  const [maxGap, setMaxGap] = useState<string>("");
  useEffect(() => {
    setGameLength(
      formatCfg?.game_duration_minutes != null
        ? String(formatCfg.game_duration_minutes)
        : ""
    );
    setTurnaround(
      formatCfg?.buffer_minutes_between_games != null
        ? String(formatCfg.buffer_minutes_between_games)
        : "0"
    );
    setMinRest(
      formatCfg?.min_rest_minutes != null ? String(formatCfg.min_rest_minutes) : "0"
    );
    setMaxPerDay(
      formatCfg?.max_games_per_day != null ? String(formatCfg.max_games_per_day) : ""
    );
    setBackToBack(
      formatCfg?.back_to_back_minutes != null ? String(formatCfg.back_to_back_minutes) : "0"
    );
    setMaxGap(
      formatCfg?.max_gap_minutes != null ? String(formatCfg.max_gap_minutes) : ""
    );
  }, [
    formatCfg?.game_duration_minutes,
    formatCfg?.buffer_minutes_between_games,
    formatCfg?.min_rest_minutes,
    formatCfg?.max_games_per_day,
    formatCfg?.back_to_back_minutes,
    formatCfg?.max_gap_minutes,
  ]);



  const sessionsQuery = useQuery({
    queryKey: ["avail-sessions", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_sessions")
        .select(
          "id, court_id, session_date, start_time, end_time, label, is_active, sort_order"
        )
        .eq("event_id", eventId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
  const sessions = sessionsQuery.data ?? [];

  /* ---------- default hours editing ---------- */
  const [defaultRows, setDefaultRows] = useState<WindowRow[] | null>(null);
  const savedDefaults: WindowRow[] = useMemo(
    () =>
      sessions
        .filter((s: any) => s.court_id === null && s.is_active !== false)
        .map((s: any) => ({
          id: s.id,
          session_date: s.session_date,
          start_time: hhmm(s.start_time),
          end_time: hhmm(s.end_time),
        })),
    [sessions]
  );
  const workingDefaults = defaultRows ?? savedDefaults;

  /* ---------- per-court editing ---------- */
  const [editingCourt, setEditingCourt] = useState<string | null>(null);
  const [courtRows, setCourtRows] = useState<WindowRow[]>([]);

  const courtOverrides = useMemo(() => {
    const map: Record<string, WindowRow[]> = {};
    for (const s of sessions) {
      if (!s.court_id || s.is_active === false) continue;
      (map[s.court_id] ||= []).push({
        id: s.id,
        session_date: s.session_date,
        start_time: hhmm(s.start_time),
        end_time: hhmm(s.end_time),
      });
    }
    return map;
  }, [sessions]);

  /* ---------- capacity ---------- */
  const glNum = gameLength === "" ? null : Number(gameLength);
  const taNum = turnaround === "" ? 0 : Number(turnaround);
  const slotMinutes = glNum && glNum > 0 ? glNum + (taNum || 0) : null;

  const previewResolved = useMemo(() => {
    // Preview reflects unsaved edits in the default editor too.
    const rows: any[] = [];
    workingDefaults.forEach((r, i) =>
      rows.push({
        id: r.id ?? `tmp-${i}`,
        court_id: null,
        session_date: r.session_date,
        start_time: r.start_time,
        end_time: r.end_time,
        is_active: true,
      })
    );
    for (const [courtId, list] of Object.entries(courtOverrides)) {
      const useRows =
        editingCourt === courtId ? courtRows : (list as WindowRow[]);
      useRows.forEach((r, i) =>
        rows.push({
          id: r.id ?? `tmpc-${courtId}-${i}`,
          court_id: courtId,
          session_date: r.session_date,
          start_time: r.start_time,
          end_time: r.end_time,
          is_active: true,
        })
      );
    }
    if (editingCourt && !courtOverrides[editingCourt]) {
      courtRows.forEach((r, i) =>
        rows.push({
          id: `tmpn-${i}`,
          court_id: editingCourt,
          session_date: r.session_date,
          start_time: r.start_time,
          end_time: r.end_time,
          is_active: true,
        })
      );
    }
    return resolveAvailability(courts ?? [], rows);
  }, [workingDefaults, courtOverrides, courts, editingCourt, courtRows]);

  const capacity = computeCapacity(previewResolved, slotMinutes);
  const totalSlots = (capacity ?? []).reduce((a, d) => a + d.slots, 0);

  /* ---------- actions ---------- */
  const saveTiming = async () => {
    setBusy(true);
    try {
      const { error } = await (supabase as any)
        .from("event_format_config")
        .upsert(
          {
            event_id: eventId,
            game_duration_minutes:
              gameLength === "" ? null : Number(gameLength),
            buffer_minutes_between_games:
              turnaround === "" ? 0 : Number(turnaround),
            min_rest_minutes: minRest === "" ? 0 : Number(minRest),
            max_games_per_day:
              maxPerDay.trim() === "" ? null : Number(maxPerDay),
            back_to_back_minutes: backToBack === "" ? 0 : Number(backToBack),
            max_gap_minutes: maxGap.trim() === "" ? null : Number(maxGap),
            updated_at: new Date().toISOString(),
          },
          { onConflict: "event_id" }
        );
      if (error) throw error;
      toast.success("Game timing saved.");
      await qc.invalidateQueries({ queryKey: ["avail-format", eventId] });

    } catch (e: any) {
      toast.error(e.message ?? "Could not save game timing");
    } finally {
      setBusy(false);
    }
  };

  const writeWindows = async (courtId: string | null, rows: WindowRow[]) => {
    const bad = rows.find(
      (r) =>
        !r.session_date ||
        !r.start_time ||
        !r.end_time ||
        windowMinutes(r.start_time, r.end_time) <= 0
    );
    if (bad) {
      toast.error("Each window needs a date and an end time after its start.");
      return false;
    }
    let del = (supabase as any)
      .from("event_sessions")
      .delete()
      .eq("event_id", eventId);
    del = courtId === null ? del.is("court_id", null) : del.eq("court_id", courtId);
    const { error: delErr } = await del;
    if (delErr) throw delErr;

    if (rows.length > 0) {
      const { error: insErr } = await (supabase as any)
        .from("event_sessions")
        .insert(
          rows.map((r, i) => ({
            event_id: eventId,
            court_id: courtId,
            session_date: r.session_date,
            start_time: r.start_time,
            end_time: r.end_time,
            is_active: true,
            sort_order: i,
          }))
        );
      if (insErr) throw insErr;
    }
    return true;
  };

  const saveDefaults = async () => {
    setBusy(true);
    try {
      if (await writeWindows(null, workingDefaults)) {
        toast.success("Default hours saved.");
        setDefaultRows(null);
        await sessionsQuery.refetch();
      }
    } catch (e: any) {
      toast.error(e.message ?? "Could not save default hours");
    } finally {
      setBusy(false);
    }
  };

  const saveCourt = async (courtId: string) => {
    setBusy(true);
    try {
      if (await writeWindows(courtId, courtRows)) {
        toast.success("Custom hours saved.");
        setEditingCourt(null);
        setCourtRows([]);
        await sessionsQuery.refetch();
      }
    } catch (e: any) {
      toast.error(e.message ?? "Could not save custom hours");
    } finally {
      setBusy(false);
    }
  };

  const resetCourt = async (courtId: string) => {
    setBusy(true);
    try {
      const { error } = await (supabase as any)
        .from("event_sessions")
        .delete()
        .eq("event_id", eventId)
        .eq("court_id", courtId);
      if (error) throw error;
      if (editingCourt === courtId) {
        setEditingCourt(null);
        setCourtRows([]);
      }
      toast.success("Court returned to the event default hours.");
      await sessionsQuery.refetch();
    } catch (e: any) {
      toast.error(e.message ?? "Could not reset court");
    } finally {
      setBusy(false);
    }
  };

  if (sessionsQuery.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const defaultsByDate: Record<string, WindowRow[]> = {};
  workingDefaults.forEach((r) => {
    (defaultsByDate[r.session_date] ||= []).push(r);
  });

  return (
    <div className="space-y-4">
      {/* -------- Game timing -------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" /> Game timing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs">Game length (minutes)</Label>
              <Input
                type="number"
                min={1}
                className="w-36"
                value={gameLength}
                onChange={(e) => setGameLength(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Turnaround (minutes)</Label>
              <Input
                type="number"
                min={0}
                className="w-36"
                value={turnaround}
                onChange={(e) => setTurnaround(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Minimum rest between a team's games (minutes)</Label>
              <Input
                type="number"
                min={0}
                className="w-36"
                value={minRest}
                onChange={(e) => setMinRest(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Maximum gap between a team's games (minutes)</Label>
              <Input
                type="number"
                min={0}
                placeholder="No limit"
                className="w-36"
                value={maxGap}
                onChange={(e) => setMaxGap(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Maximum games per team per day</Label>
              <Input
                type="number"
                min={1}
                placeholder="No limit"
                className="w-36"
                value={maxPerDay}
                onChange={(e) => setMaxPerDay(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Count as back-to-back at or under (minutes)</Label>
              <Input
                type="number"
                min={0}
                className="w-36"
                value={backToBack}
                onChange={(e) => setBackToBack(e.target.value)}
              />
            </div>
            <Button onClick={saveTiming} disabled={busy}>
              <Save className="mr-1 h-4 w-4" /> Save
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            <strong>Minimum rest</strong> is the shortest gap a team may have between
            games — a hard rule, never broken. <strong>Maximum gap</strong> is the
            longest they should have to wait — a preference. The scheduler tries every
            slot that keeps teams inside it first, but it will exceed it rather than
            leave a game unplaced, and it tells you when it did. Leave it blank for no limit.
          </p>

          <p className="text-sm text-muted-foreground">
            {slotMinutes
              ? `${glNum} + ${taNum || 0} = a game starts every ${slotMinutes} minutes.`
              : "Set a game length to work out the slot increment."}
          </p>
        </CardContent>
      </Card>

      {/* -------- Default hours -------- */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Default hours</CardTitle>
          <div className="flex gap-2">
            {defaultRows && (
              <Button size="sm" variant="ghost" onClick={() => setDefaultRows(null)}>
                Cancel
              </Button>
            )}
            <Button size="sm" onClick={saveDefaults} disabled={busy}>
              <Save className="mr-1 h-4 w-4" /> Save hours
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            These windows apply to every court unless a court has custom hours
            for that date.
          </p>
          <WindowEditor
            rows={workingDefaults}
            onChange={setDefaultRows}
            defaultDate={event?.start_date}
          />
        </CardContent>
      </Card>

      {/* -------- Per-court hours -------- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-court hours</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(courts ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              No courts on this event yet.
            </p>
          )}
          {(courts ?? []).map((c: any) => {
            const overrides = courtOverrides[c.id] ?? [];
            const isEditing = editingCourt === c.id;
            return (
              <div key={c.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{c.name}</span>
                    {overrides.length === 0 ? (
                      <Badge variant="outline">Uses default</Badge>
                    ) : (
                      <Badge variant="secondary">
                        Custom —{" "}
                        {overrides
                          .map(
                            (o) =>
                              `${formatCalendarDate(o.session_date, "EEE MMM d")} ${formatTime(
                                o.start_time
                              )}–${formatTime(o.end_time)}`
                          )
                          .join(", ")}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!isEditing && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingCourt(c.id);
                          setCourtRows(
                            overrides.length > 0
                              ? overrides
                              : workingDefaults.map((r) => ({
                                  session_date: r.session_date,
                                  start_time: r.start_time,
                                  end_time: r.end_time,
                                }))
                          );
                        }}
                      >
                        Set custom hours
                      </Button>
                    )}
                    {overrides.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => resetCourt(c.id)}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" /> Reset to default
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-3 space-y-3 border-t pt-3">
                    <WindowEditor
                      rows={courtRows}
                      onChange={setCourtRows}
                      defaultDate={event?.start_date}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy} onClick={() => saveCourt(c.id)}>
                        <Save className="mr-1 h-4 w-4" /> Save custom hours
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingCourt(null);
                          setCourtRows([]);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Custom hours replace the default for this court on the
                      dates listed here. Dates you leave out keep the event
                      default.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* -------- Capacity preview -------- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Capacity preview</CardTitle>
        </CardHeader>
        <CardContent>
          {!capacity ? (
            <p className="text-sm text-muted-foreground">
              Set a game length to see capacity
            </p>
          ) : capacity.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Add some hours to see capacity.
            </p>
          ) : (
            <div className="space-y-1 text-sm">
              {capacity.map((d) => (
                <div key={d.date}>
                  <span className="font-medium">
                    {formatCalendarDate(d.date, "EEE MMM d")}
                  </span>{" "}
                  — {d.courts} court{d.courts === 1 ? "" : "s"} ×{" "}
                  {durationLabel(d.courts ? Math.round(d.minutes / d.courts) : 0)} ÷{" "}
                  {slotMinutes} min ={" "}
                  <span className="font-semibold">{d.slots} slots</span>
                </div>
              ))}
              <div className="pt-2 font-semibold">{totalSlots} slots total</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
